import React, { useState, useEffect } from 'react';

const Settings = ({ onClose, onSettingsChange, currentSettings, isMobile = false }) => {
    const [settings, setSettings] = useState(currentSettings);
    const [availableImages, setAvailableImages] = useState([]);

    useEffect(() => {
        // Load available images from the backgrounds folder
        const loadAvailableImages = async () => {
            try {
                // This would typically be done by scanning the public/images/backgrounds folder
                // For now, we'll provide some sample image options
                const sampleImages = [
                    { name: 'Default Background', path: '/images/backgrounds/bg.png' },
                    { name: 'Cyber Grid', path: '/images/backgrounds/cyber-grid.png.jpg' },
                    { name: 'Neon Sunset', path: '/images/backgrounds/neon-sunset.jpg' },
                    { name: 'Retro Waves', path: '/images/backgrounds/retro-waves.gif' },
                    { name: 'Pixel Hearts', path: '/images/backgrounds/pixel-hearts.png' },
                    { name: 'Cyber City', path: '/images/backgrounds/cyber-city.jpg' }
                ];
                setAvailableImages(sampleImages);
            } catch (error) {
                console.log('No custom images found in backgrounds folder');
            }
        };

        loadAvailableImages();
    }, []);

    const handleFontSizeChange = (size) => {
        const newSettings = { ...settings, fontSize: size };
        setSettings(newSettings);
        onSettingsChange(newSettings);
    };

    const handleIconSizeChange = (size) => {
        const newSettings = { ...settings, iconSize: size };
        setSettings(newSettings);
        onSettingsChange(newSettings);
    };

    const handleBackgroundChange = (background) => {
        const newSettings = { ...settings, background };
        setSettings(newSettings);
        onSettingsChange(newSettings);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                alert('Please select a valid image file (JPG, PNG, GIF, or WebP)');
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('Image file is too large. Please select an image smaller than 10MB.');
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const newSettings = { ...settings, background: event.target.result };
                setSettings(newSettings);
                onSettingsChange(newSettings);
            };
            reader.readAsDataURL(file);
        }
    };

    const recommendedColors = [
        { name: 'Y2K Blue', value: '#00BFFF', gradient: 'linear-gradient(135deg, #00BFFF 0%, #0080FF 100%)' },
        { name: 'Cyber Pink', value: '#FF69B4', gradient: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)' },
        { name: 'Neon Green', value: '#00FF7F', gradient: 'linear-gradient(135deg, #00FF7F 0%, #32CD32 100%)' },
        { name: 'Purple Haze', value: '#9370DB', gradient: 'linear-gradient(135deg, #9370DB 0%, #8A2BE2 100%)' },
        { name: 'Sunset Orange', value: '#FF6347', gradient: 'linear-gradient(135deg, #FF6347 0%, #FF4500 100%)' },
        { name: 'Cyber Teal', value: '#20B2AA', gradient: 'linear-gradient(135deg, #20B2AA 0%, #008B8B 100%)' }
    ];

    const recommendedPatterns = [
        { name: 'Classic Windows 98', value: 'windows98' },
        { name: 'Y2K Grid', value: 'y2kgrid' },
        { name: 'Cyber Waves', value: 'cyberwaves' },
        { name: 'Neon Dots', value: 'neondots' }
    ];

    return (
        <div className="window-overlay">
            <div
                className={`win98-window settings-window ${isMobile ? 'mobile-fullscreen-window' : ''}`}
                style={isMobile ? {
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    width: '100vw',
                    height: '100vh',
                    transform: 'none'
                } : {
                    position: 'absolute',
                    left: 200,
                    top: 100,
                    width: 500,
                    height: 400
                }}
            >
                <div className="window-titlebar">
                    <div className="window-title">GOOD VIBES* #ccfc Settings</div>
                    <div className="window-controls">
                        <button className="minimize-btn">_</button>
                        <button className="maximize-btn">□</button>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                </div>

                <div className="window-content">
                    <div className="win98-content">
                        <div className="settings-sections">

                            {/* Font Size Settings */}
                            <div className="settings-section">
                                <h3>Font Size</h3>
                                <div className="slider-container">
                                    <input
                                        type="range"
                                        min="10"
                                        max="20"
                                        value={settings.fontSize}
                                        onChange={(e) => handleFontSizeChange(parseInt(e.target.value))}
                                        className="win98-slider"
                                    />
                                    <span className="slider-value">{settings.fontSize}px</span>
                                </div>
                            </div>

                            {/* Icon Size Settings */}
                            <div className="settings-section">
                                <h3>Icon Size</h3>
                                <div className="slider-container">
                                    <input
                                        type="range"
                                        min="32"
                                        max="96"
                                        value={settings.iconSize}
                                        onChange={(e) => handleIconSizeChange(parseInt(e.target.value))}
                                        className="win98-slider"
                                    />
                                    <span className="slider-value">{settings.iconSize}px</span>
                                </div>
                            </div>

                            {/* Background Settings */}
                            <div className="settings-section">
                                <h3>Background</h3>

                                {/* Recommended Colors */}
                                <div className="color-options">
                                    <h4>Recommended Colors</h4>
                                    <div className="color-grid">
                                        {recommendedColors.map((color, index) => (
                                            <button
                                                key={index}
                                                className="color-option"
                                                style={{ background: color.gradient }}
                                                onClick={() => handleBackgroundChange(color.value)}
                                                title={color.name}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Recommended Patterns */}
                                <div className="pattern-options">
                                    <h4>Recommended Patterns</h4>
                                    <div className="pattern-grid">
                                        {recommendedPatterns.map((pattern, index) => (
                                            <button
                                                key={index}
                                                className="pattern-option"
                                                onClick={() => handleBackgroundChange(pattern.value)}
                                            >
                                                {pattern.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Available Images from Folder */}
                                {availableImages.length > 0 && (
                                    <div className="available-images-section">
                                        <h4>Available Images</h4>
                                        <div className="image-grid">
                                            {availableImages.map((image, index) => (
                                                <button
                                                    key={index}
                                                    className="image-option"
                                                    onClick={() => handleBackgroundChange(image.path)}
                                                    title={image.name}
                                                >
                                                    <div
                                                        className="image-thumbnail"
                                                        style={{
                                                            backgroundImage: `url(${image.path})`,
                                                            backgroundSize: 'cover',
                                                            backgroundPosition: 'center'
                                                        }}
                                                    />
                                                    <span className="image-name">{image.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Custom Image Upload */}
                                <div className="upload-section">
                                    <h4>Upload Your Own Image</h4>
                                    <div className="upload-container">
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            onChange={handleImageUpload}
                                            className="win98-file-input"
                                            id="background-upload"
                                        />
                                        <label htmlFor="background-upload" className="win98-button upload-button">
                                            📁 Choose Image
                                        </label>
                                        <div className="upload-info">
                                            <small>Supported: JPG, PNG, GIF, WebP (max 10MB)</small>
                                        </div>
                                        {settings.background && settings.background.startsWith('data:') && (
                                            <div className="image-preview">
                                                <h5>Current Background Preview:</h5>
                                                <div
                                                    className="preview-image"
                                                    style={{
                                                        backgroundImage: `url(${settings.background})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center'
                                                    }}
                                                />
                                                <button
                                                    className="win98-button small"
                                                    onClick={() => {
                                                        const newSettings = { ...settings, background: 'url(/images/backgrounds/bg.png) center/cover no-repeat' };
                                                        setSettings(newSettings);
                                                        onSettingsChange(newSettings);
                                                    }}
                                                >
                                                    Reset to Default
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
